import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Meta } from '../components/Meta';
import {
  Check,
  X,
  ArrowRight,
  Users,
  BookOpen,
  Video,
  TreePine,
  Calendar,
  Building,
  Search,
  FileText,
  Sparkles,
  Heart,
  Globe,
  MessageCircle,
  Star,
  Zap,
  Gift,
  Shield,
  Clock,
  MapPin
} from 'lucide-react';
import { Auth } from '../components/Auth';

export function Pricing() {
  const { t } = useTranslation();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleGetStarted = () => {
    setShowAuthModal(true);
  };

  const freeFeatures = [
    { icon: Search, titleKey: 'pricing.discoverPractitioners', descKey: 'pricing.discoverPractitionersDesc' },
    { icon: MapPin, titleKey: 'pricing.findNearYou', descKey: 'pricing.findNearYouDesc' },
    { icon: FileText, titleKey: 'pricing.unlimitedArticles', descKey: 'pricing.unlimitedArticlesDesc' },
    { icon: MessageCircle, titleKey: 'pricing.joinCreateCommunities', descKey: 'pricing.joinCreateCommunitiesDesc' },
    { icon: Calendar, titleKey: 'pricing.browseEvents', descKey: 'pricing.browseEventsDesc' },
    { icon: Heart, titleKey: 'pricing.saveFavorites', descKey: 'pricing.saveFavoritesDesc' }
  ];

  const premiumFeatures = [
    { icon: Video, titleKey: 'pricing.unlimitedLive', descKey: 'pricing.unlimitedLiveDesc', highlight: true },
    { icon: BookOpen, titleKey: 'pricing.fullCourseLibrary', descKey: 'pricing.fullCourseLibraryDesc' },
    { icon: TreePine, titleKey: 'pricing.agriTools', descKey: 'pricing.agriToolsDesc' },
    { icon: Building, titleKey: 'pricing.proListings', descKey: 'pricing.proListingsDesc' },
    { icon: Sparkles, titleKey: 'pricing.jobBoard', descKey: 'pricing.jobBoardDesc' },
    { icon: Gift, titleKey: 'pricing.exclusiveDiscounts', descKey: 'pricing.exclusiveDiscountsDesc' }
  ];

  const comparisonFeatures = [
    { nameKey: 'pricing.browsePractitioners', free: true, premium: true, icon: Search },
    { nameKey: 'pricing.readArticles', free: true, premium: true, icon: FileText },
    { nameKey: 'pricing.joinCreateComm', free: true, premium: true, icon: Users },
    { nameKey: 'pricing.browseEventsVenues', free: true, premium: true, icon: Calendar },
    { nameKey: 'pricing.nearMeFeatures', free: true, premium: true, icon: MapPin },
    { nameKey: 'pricing.saveBookmarks', free: true, premium: true, icon: Heart },
    { nameKey: 'pricing.unlimitedLiveSessions', free: false, premium: true, icon: Video },
    { nameKey: 'pricing.fullCourseAccess', free: false, premium: true, icon: BookOpen },
    { nameKey: 'pricing.agriGardenTools', free: false, premium: true, icon: TreePine },
    { nameKey: 'pricing.listAsPractitioner', free: false, premium: true, icon: Building },
    { nameKey: 'pricing.accessJobBoard', free: false, premium: true, icon: Sparkles },
    { nameKey: 'pricing.exclusiveMemberDiscounts', free: false, premium: true, icon: Gift }
  ];

  const testimonials = [
    { quoteKey: 'pricing.testimonial1', author: 'Sarah M.', roleKey: 'pricing.premiumMember' },
    { quoteKey: 'pricing.testimonial2', author: 'James K.', roleKey: 'pricing.freeMember' },
    { quoteKey: 'pricing.testimonial3', author: 'Emma L.', roleKey: 'pricing.premiumMember' }
  ];

  return (
    <div className="min-h-screen">
      <Meta
        title={t('pricing.metaTitle')}
        description={t('pricing.metaDesc')}
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-[#F3F7EE] via-[#F8FAF5] to-white py-16 md:py-24 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-accent-text/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent-text/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-accent-text/10 rounded-full mb-6">
              <Zap className="h-4 w-4 text-accent-text mr-2" />
              <span className="text-sm font-medium text-accent-text">{t('pricing.tagline')}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-gelica font-bold leading-tight mb-6" style={{ color: '#2F4F3E' }}>
              {t('pricing.title')}
            </h1>
            <p className="text-xl md:text-2xl mb-4" style={{ color: '#5A6B5A' }}>
              {t('pricing.subtitle')}
            </p>
            <p className="text-lg md:text-xl mb-8" style={{ color: '#5A6B5A' }}>
              <span className="font-semibold text-accent-text">{t('pricing.freeForever')}</span> with powerful features, or unlock everything for just <span className="font-semibold text-accent-text">{t('pricing.priceMonthly')}</span>
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleGetStarted}
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium rounded-full text-white bg-accent-text hover:bg-accent-text/90 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {t('pricing.startFreeToday')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <a
                href="#comparison"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium rounded-full border-2 border-accent-text text-accent-text hover:bg-accent-text hover:text-white transition-all"
              >
                {t('pricing.comparePlans')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Free Tier Showcase */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-green-100 rounded-full mb-4">
              <Gift className="h-4 w-4 text-green-600 mr-2" />
              <span className="text-sm font-medium text-green-700">{t('pricing.foreverFreeTitle')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-gelica font-bold mb-4" style={{ color: '#2F4F3E' }}>
              {t('pricing.foreverFreeSubtitle')}
            </h2>
            <p className="text-xl max-w-2xl mx-auto" style={{ color: '#5A6B5A' }}>
              {t('pricing.foreverFreeDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {freeFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-[#F3F7EE] to-white p-6 rounded-2xl border border-accent-text/10 hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-accent-text/10 rounded-xl mb-4 group-hover:bg-accent-text/20 transition-colors">
                    <Icon className="h-6 w-6 text-accent-text" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#2F4F3E' }}>
                    {t(feature.titleKey)}
                  </h3>
                  <p className="text-sm" style={{ color: '#5A6B5A' }}>
                    {t(feature.descKey)}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="text-center">
            <button
              onClick={handleGetStarted}
              className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-full border-2 border-accent-text text-accent-text hover:bg-accent-text hover:text-white transition-all"
            >
              {t('pricing.joinFreeNoCc')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Premium Tier Showcase */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#F3F7EE] to-white relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-accent-text/5 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2"></div>

        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-accent-text/10 rounded-full mb-4">
                <Star className="h-4 w-4 text-accent-text mr-2" />
                <span className="text-sm font-medium text-accent-text">{t('pricing.premiumTitle')}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-gelica font-bold mb-4" style={{ color: '#2F4F3E' }}>
                {t('pricing.premiumSubtitle')}
              </h2>
              <p className="text-xl mb-6" style={{ color: '#5A6B5A' }}>
                {t('pricing.premiumDesc')}
              </p>

              <div className="flex items-baseline gap-2 mb-8">
                <span className="text-5xl md:text-6xl font-bold" style={{ color: '#2F4F3E' }}>£5</span>
                <span className="text-xl" style={{ color: '#5A6B5A' }}>/month</span>
                <span className="ml-4 px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                  {t('pricing.cancelAnytime')}
                </span>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <Check className="h-4 w-4 text-green-600" />
                  </div>
                  <span style={{ color: '#2F4F3E' }}>{t('pricing.everythingInFree')}</span>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <Check className="h-4 w-4 text-green-600" />
                  </div>
                  <span style={{ color: '#2F4F3E' }}>{t('pricing.liveSessions')}</span>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <Check className="h-4 w-4 text-green-600" />
                  </div>
                  <span style={{ color: '#2F4F3E' }}>{t('pricing.courseLibrary')}</span>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <Check className="h-4 w-4 text-green-600" />
                  </div>
                  <span style={{ color: '#2F4F3E' }}>{t('pricing.listServices')}</span>
                </div>
              </div>

              <button
                onClick={handleGetStarted}
                className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-full text-white bg-accent-text hover:bg-accent-text/90 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {t('pricing.becomePremium')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {premiumFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className={`p-5 rounded-xl border transition-all duration-300 hover:shadow-lg ${
                      feature.highlight
                        ? 'bg-accent-text text-white border-accent-text'
                        : 'bg-white border-accent-text/20 hover:border-accent-text/40'
                    }`}
                  >
                    <Icon className={`h-6 w-6 mb-3 ${feature.highlight ? 'text-white' : 'text-accent-text'}`} />
                    <h3 className={`font-semibold mb-1 ${feature.highlight ? 'text-white' : ''}`} style={!feature.highlight ? { color: '#2F4F3E' } : {}}>
                      {t(feature.titleKey)}
                    </h3>
                    <p className={`text-sm ${feature.highlight ? 'text-white/90' : ''}`} style={!feature.highlight ? { color: '#5A6B5A' } : {}}>
                      {t(feature.descKey)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Value Comparison */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-gelica font-bold mb-4" style={{ color: '#2F4F3E' }}>
              Premium Pays for Itself
            </h2>
            <p className="text-xl" style={{ color: '#5A6B5A' }}>
              See how much value you get for just £5/month
            </p>
          </div>

          <div className="bg-gradient-to-br from-[#F3F7EE] to-white rounded-2xl p-8 border border-accent-text/10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="p-4">
                <div className="text-3xl font-bold text-accent-text mb-2">12-16</div>
                <div className="text-sm font-medium" style={{ color: '#2F4F3E' }}>{t('pricing.liveSessionsMonth')}</div>
                <div className="text-xs mt-1" style={{ color: '#5A6B5A' }}>{t('pricing.worthStudios')}</div>
              </div>
              <div className="p-4 border-y md:border-y-0 md:border-x border-accent-text/10">
                <div className="text-3xl font-bold text-accent-text mb-2">Unlimited</div>
                <div className="text-sm font-medium" style={{ color: '#2F4F3E' }}>{t('pricing.courseAccess')}</div>
                <div className="text-xs mt-1" style={{ color: '#5A6B5A' }}>{t('pricing.learnPace')}</div>
              </div>
              <div className="p-4">
                <div className="text-3xl font-bold text-accent-text mb-2">10-20%</div>
                <div className="text-sm font-medium" style={{ color: '#2F4F3E' }}>{t('pricing.offRetreats')}</div>
                <div className="text-xs mt-1" style={{ color: '#5A6B5A' }}>{t('pricing.memberDiscounts')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-[#F3F7EE]">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-gelica font-bold mb-4" style={{ color: '#2F4F3E' }}>
              {t('pricing.lovedByCommunity')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="mb-4 italic" style={{ color: '#5A6B5A' }}>
                  "{t(testimonial.quoteKey)}"
                </p>
                <div>
                  <div className="font-semibold" style={{ color: '#2F4F3E' }}>{testimonial.author}</div>
                  <div className="text-sm text-accent-text">{t(testimonial.roleKey)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Comparison Table */}
      <section id="comparison" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-gelica font-bold mb-4" style={{ color: '#2F4F3E' }}>
              {t('pricing.compareSideBySide')}
            </h2>
            <p className="text-xl" style={{ color: '#5A6B5A' }}>
              {t('pricing.compareSideBySideDesc')}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-[#F3F7EE] to-white">
                    <th className="text-left py-6 px-6 font-semibold text-lg" style={{ color: '#2F4F3E' }}>
                      {t('pricing.featuresColumn')}
                    </th>
                    <th className="text-center py-6 px-6">
                      <div className="font-semibold text-lg" style={{ color: '#2F4F3E' }}>{t('pricing.freeColumn')}</div>
                      <div className="text-2xl font-bold mt-1" style={{ color: '#2F4F3E' }}>£0</div>
                      <div className="text-sm" style={{ color: '#5A6B5A' }}>{t('pricing.forever')}</div>
                    </th>
                    <th className="text-center py-6 px-6 bg-accent-text/10 rounded-t-xl">
                      <div className="font-semibold text-lg" style={{ color: '#2F4F3E' }}>{t('pricing.premiumColumn')}</div>
                      <div className="text-2xl font-bold mt-1" style={{ color: '#2F4F3E' }}>£5</div>
                      <div className="text-sm" style={{ color: '#5A6B5A' }}>{t('pricing.perMonthLabel')}</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <tr
                        key={index}
                        className={`border-t border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                      >
                        <td className="py-4 px-6">
                          <div className="flex items-center">
                            <Icon className="h-5 w-5 text-accent-text mr-3 flex-shrink-0" />
                            <span className="font-medium" style={{ color: '#2F4F3E' }}>
                              {t(feature.nameKey)}
                            </span>
                          </div>
                        </td>
                        <td className="text-center py-4 px-6">
                          {feature.free ? (
                            <div className="inline-flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
                              <Check className="h-5 w-5 text-green-600" />
                            </div>
                          ) : (
                            <div className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full">
                              <X className="h-5 w-5 text-gray-400" />
                            </div>
                          )}
                        </td>
                        <td className={`text-center py-4 px-6 ${index % 2 === 0 ? 'bg-accent-text/5' : 'bg-accent-text/10'}`}>
                          <div className="inline-flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
                            <Check className="h-5 w-5 text-green-600" />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Table Footer CTAs */}
            <div className="grid grid-cols-1 md:grid-cols-3 border-t border-gray-100">
              <div className="p-6"></div>
              <div className="p-6 flex justify-center border-t md:border-t-0 md:border-l border-gray-100">
                <button
                  onClick={handleGetStarted}
                  className="px-6 py-3 border-2 border-accent-text text-accent-text rounded-xl font-medium hover:bg-accent-text hover:text-white transition-colors"
                >
                  {t('pricing.getStartedFree')}
                </button>
              </div>
              <div className="p-6 flex justify-center bg-accent-text/5 border-t md:border-t-0 md:border-l border-gray-100">
                <button
                  onClick={handleGetStarted}
                  className="px-6 py-3 bg-accent-text text-white rounded-xl font-medium hover:bg-accent-text/90 transition-colors shadow-md"
                >
                  {t('pricing.goPremium')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-[#F3F7EE]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-gelica font-bold mb-4" style={{ color: '#2F4F3E' }}>
              {t('pricing.commonQuestions')}
            </h2>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-2 flex items-center" style={{ color: '#2F4F3E' }}>
                <Gift className="h-5 w-5 text-accent-text mr-2" />
                {t('pricing.faqFreeQ')}
              </h3>
              <p style={{ color: '#5A6B5A' }}>
                {t('pricing.faqFreeA')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-2 flex items-center" style={{ color: '#2F4F3E' }}>
                <Video className="h-5 w-5 text-accent-text mr-2" />
                {t('pricing.faqLiveQ')}
              </h3>
              <p style={{ color: '#5A6B5A' }}>
                {t('pricing.faqLiveA')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-2 flex items-center" style={{ color: '#2F4F3E' }}>
                <Shield className="h-5 w-5 text-accent-text mr-2" />
                {t('pricing.faqCancelQ')}
              </h3>
              <p style={{ color: '#5A6B5A' }}>
                {t('pricing.faqCancelA')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-2 flex items-center" style={{ color: '#2F4F3E' }}>
                <Building className="h-5 w-5 text-accent-text mr-2" />
                {t('pricing.faqPractitionerQ')}
              </h3>
              <p style={{ color: '#5A6B5A' }}>
                {t('pricing.faqPractitionerA')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-2 flex items-center" style={{ color: '#2F4F3E' }}>
                <Clock className="h-5 w-5 text-accent-text mr-2" />
                {t('pricing.faqAffordableQ')}
              </h3>
              <p style={{ color: '#5A6B5A' }}>
                {t('pricing.faqAffordableA')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#F3F7EE] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-accent-text/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent-text/5 rounded-full blur-3xl"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center relative z-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-text/10 rounded-full mb-6">
            <Heart className="w-8 h-8 text-accent-text" />
          </div>
          <h2 className="text-3xl md:text-4xl font-gelica font-bold mb-6" style={{ color: '#2F4F3E' }}>
            {t('pricing.joinThousands')}
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto" style={{ color: '#5A6B5A' }}>
            {t('pricing.joinThousandsDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleGetStarted}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium rounded-full text-white bg-accent-text hover:bg-accent-text/90 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              {t('pricing.startJourneyFree')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <Link
              to="/live-stream"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium rounded-full border-2 border-accent-text text-accent-text hover:bg-accent-text hover:text-white transition-all"
            >
              {t('pricing.previewLive')}
            </Link>
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-sm" style={{ color: '#5A6B5A' }}>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-accent-text" />
              <span>{t('pricing.noCreditCard')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-accent-text" />
              <span>{t('pricing.cancelAnytime')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-accent-text" />
              <span>{t('pricing.globalCommunity')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Auth Modal */}
      {showAuthModal && (
        <Auth onClose={() => setShowAuthModal(false)} />
      )}
    </div>
  );
}

export default Pricing;
